import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { FileCheck2 } from "lucide-react";
import {
  ApiError,
  createConsentWithdrawal,
  getMyConsents,
  reaffirmMyConsents,
} from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import type {
  InviteConsents,
  MyConsentRecord,
  PendingConsentAffirmation,
} from "@/lib/types";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useToast } from "@/components/ui/toast";
import {
  ConsentPanel,
  EMPTY_CONSENTS,
  requiredConsentsMet,
} from "@/features/onboarding/components/ConsentPanel";

/**
 * The `/me?tab=consent` surface — the parent's own consent record
 * (docs/plans/consent-visibility-plan.md piece 1) plus the re-consent
 * and withdrawal-request controls (piece 4).
 *
 * PRIVACY (docs/PRIVACY.md, asserted by tests/e2e/consent-visibility.spec.js
 * — written before this renderer): everything shown here comes from an
 * explicit allowlist of fields on the caller's OWN rows. The operator's
 * `note` / `recorded_by_user_id` are never templated even if a payload
 * carries them, and a co-guardian's affirmation for the same child is
 * server-side invisible to this endpoint.
 */

/** Plain-language names for the stored documents and per-child items —
 * raw payload keys must never reach the DOM. */
const DOC_LABELS: Record<string, string> = {
  "beta-participation-agreement": "Beta Participation Agreement",
  "beta-privacy-notice": "Beta Privacy Notice",
  "parental-consent": "Parental consent",
};

const ITEM_LABELS: Record<string, string> = {
  roster: "Roster entry",
  video: "Match & training video",
  feedback: "Coaching feedback",
  photo: "Profile photo (optional)",
};

function formatDate(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function ConsentTab() {
  const query = useQuery({
    queryKey: unscopedKey("me-consents"),
    queryFn: getMyConsents,
  });

  if (query.isLoading) {
    return (
      <div data-testid="me-consent-tab" className="text-sm text-ink-muted">
        Loading your consent record…
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <div data-testid="me-consent-tab" className="text-sm text-ink-muted">
        Couldn't load your consent record. Try again in a moment.
      </div>
    );
  }

  const { records, needs_affirmation } = query.data;
  // Withdrawal eligibility comes from the server's MEMBERSHIP-derived
  // list, not from the caller's own consent rows. Deriving it from
  // records hid the control from the families most likely to want it:
  // offline-recorded consent stores user_id NULL, pre-capture and
  // roster-imported guardians have no rows at all, player-role members
  // never hold parental rows, and a co-guardian is never prompted so
  // never accumulates one.
  const withdrawableTeams = (query.data.withdrawable_teams ?? []).map(
    (team) => ({
      team_id: team.team_id,
      team_name: team.team_name,
      players: team.players.map((p) => ({ id: p.id, name: p.display_name })),
    }),
  );

  return (
    <div data-testid="me-consent-tab" className="account-two-column">
      <div className="account-main-stack">
        <section className="account-pane">
          <div className="account-pane-heading">
            <div>
              <h2>Your consent record</h2>
              <p className="account-support-copy">
                What you've agreed to during the beta, with its date and
                document version. Only your own affirmations appear here.
              </p>
            </div>
            <FileCheck2 aria-hidden="true" />
          </div>

          {needs_affirmation.map((group) => (
            <ReaffirmPanel key={group.team_id} group={group} />
          ))}

          {records.length === 0 && needs_affirmation.length === 0 ? (
            <p className="account-empty-copy">
              No consent records yet. When you accept a team invite or affirm
              the beta documents, your record appears here.
            </p>
          ) : (
            <ul className="account-consent-list">
              {records.map((record) => (
                <ConsentRecordRow key={record.id} record={record} />
              ))}
            </ul>
          )}
        </section>

        {withdrawableTeams.map((team) => (
          <WithdrawSection key={team.team_id} team={team} />
        ))}
      </div>
      <aside className="account-cyan-panel">
        <h2>
          Your choices,
          <br />
          on record.
        </h2>
        <p>Review the beta documents at any time.</p>
        <Link to="/privacy" className="account-tonal-link">
          Read privacy notice
        </Link>
        <Link to="/terms" className="account-tonal-link">
          Read beta agreement
        </Link>
      </aside>
    </div>
  );
}

function ConsentRecordRow({ record }: { record: MyConsentRecord }) {
  const isParental = record.document === "parental-consent";
  const title = isParental
    ? `${DOC_LABELS[record.document]} — ${record.player_name ?? "your player"}`
    : (DOC_LABELS[record.document] ?? "Beta document");
  return (
    <li
      data-testid="me-consent-record"
      data-document={record.document}
      className="rounded border border-rule bg-surface p-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-ink">{title}</span>
        <span
          data-current={record.is_current}
          className="rounded-full border border-rule px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-ink-muted data-[current=true]:border-good/40 data-[current=true]:text-good"
        >
          {record.is_current ? "Current" : "Superseded"}
        </span>
      </div>
      <p className="mt-1 text-xs text-ink-muted">
        {record.team_name} · agreed {formatDate(record.created_at)} · version{" "}
        <span className="font-mono">{record.version}</span>
      </p>
      <p className="mt-0.5 text-xs text-ink-muted">
        {record.source === "email"
          ? "Recorded by the club from your written confirmation."
          : "Affirmed by you in the app."}
      </p>
      {isParental ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {Object.entries(ITEM_LABELS).map(([key, label]) => (
            <span
              key={key}
              data-testid={`me-consent-item-${key}`}
              data-affirmed={record.items[key] === true}
              className="rounded-full border border-rule px-2 py-0.5 text-[0.65rem] text-ink-muted data-[affirmed=true]:border-good/40 data-[affirmed=true]:text-good data-[affirmed=false]:opacity-70"
            >
              {label}: {record.items[key] === true ? "agreed" : "not agreed"}
            </span>
          ))}
        </div>
      ) : null}
    </li>
  );
}

function ReaffirmPanel({ group }: { group: PendingConsentAffirmation }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [consents, setConsents] = useState<InviteConsents>(EMPTY_CONSENTS);
  const playerCount = group.role === "guardian" ? group.players.length : 0;
  const ready = requiredConsentsMet(consents, playerCount);

  const mut = useMutation({
    mutationFn: () =>
      reaffirmMyConsents(
        group.team_id,
        consents,
        group.players.map((p) => p.id),
      ),
    onSuccess: async () => {
      toast.success("Consent confirmed", {
        description: `Thanks — your consent for ${group.team_name} is up to date.`,
      });
      await queryClient.invalidateQueries({
        queryKey: unscopedKey("me-consents"),
      });
    },
    onError: (err) =>
      toast.error("Couldn't record consent", {
        description:
          err instanceof ApiError && err.status === 409
            ? err.message
            : "Please try again, or contact the club.",
      }),
  });

  return (
    <section
      data-testid="me-consent-reaffirm"
      data-status={group.status}
      data-team={group.team_id}
      className="flex flex-col gap-3 rounded border border-accent/40 bg-accent/5 p-3"
    >
      <div>
        <h3 className="text-sm font-medium text-ink">
          {group.status === "stale"
            ? `The beta documents were updated — please re-confirm for ${group.team_name}.`
            : `Consent is needed for ${group.team_name}.`}
        </h3>
        <p className="mt-1 text-xs text-ink-muted">
          {playerCount > 0
            ? `This covers ${group.players.map((p) => p.display_name).join(", ")}.`
            : "This covers your own participation."}
        </p>
      </div>
      <ConsentPanel
        playerCount={playerCount}
        consents={consents}
        onChange={setConsents}
      />
      <div>
        <Button
          type="button"
          data-testid="me-consent-reaffirm-submit"
          disabled={!ready || mut.isPending}
          onClick={() => mut.mutate()}
        >
          {mut.isPending ? "Recording…" : "Confirm consent"}
        </Button>
      </div>
    </section>
  );
}

function WithdrawSection({
  team,
}: {
  team: {
    team_id: string;
    team_name: string;
    players: { id: string; name: string }[];
  };
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [playerId, setPlayerId] = useState<string>("");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const mut = useMutation({
    mutationFn: () =>
      createConsentWithdrawal({
        team_id: team.team_id,
        player_id: playerId || null,
        message,
      }),
    onSuccess: (result) => {
      setSubmitted(true);
      setOpen(false);
      if (result.already_pending) {
        toast.success("Request already on file", {
          description: "The club has your earlier withdrawal request.",
        });
      }
    },
    onError: () =>
      toast.error("Couldn't send the request", {
        description: "Please try again, or contact the club directly.",
      }),
  });

  return (
    <section className="account-pane account-withdraw-pane">
      <h3 className="text-sm font-medium text-ink">
        Withdrawing consent — {team.team_name}
      </h3>
      <p className="mt-1 text-xs leading-5 text-ink-muted">
        You can withdraw consent at any time. This sends a request to the club:
        your child's profile and feedback are removed, and match video is
        deleted in full on request (one player can't be edited out of group
        footage). Nothing is deleted automatically.
      </p>
      {submitted ? (
        <p
          data-testid="consent-withdraw-confirmation"
          className="mt-2 rounded border border-good/40 bg-good/5 p-2 text-xs text-ink"
        >
          Your withdrawal request has been recorded. The club will contact you
          to confirm what happens next — nothing has been deleted yet.
        </p>
      ) : (
        <div className="mt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="me-consent-withdraw"
            onClick={() => setOpen(true)}
          >
            Request withdrawal…
          </Button>
        </div>
      )}
      {open ? (
        <ConfirmDialog
          title="Request consent withdrawal"
          data-testid="consent-withdraw-dialog"
          confirmTestId="consent-withdraw-submit"
          confirmLabel={mut.isPending ? "Sending…" : "Send withdrawal request"}
          confirmDisabled={mut.isPending}
          initialFocus="close"
          onClose={() => setOpen(false)}
          onConfirm={() => mut.mutate()}
          message={
            <div className="account-withdraw-dialog-body">
              <p>
                Ask the club to withdraw consent. The club reviews the request;
                this form does not delete any records or recordings.
              </p>
              <label className="account-withdraw-field">
                <span>Who this request covers</span>
                <TkticSelect
                  ariaLabel="Who this request covers"
                  value={playerId}
                  onChange={setPlayerId}
                  options={[
                    {
                      value: "",
                      label:
                        team.players.length === 1
                          ? `${team.players[0]?.name ?? "Linked player"} · ${team.team_name}`
                          : team.players.length === 2
                            ? `Both linked players · ${team.team_name}`
                            : `All ${team.players.length} linked players · ${team.team_name}`,
                    },
                    ...team.players.map((p) => ({
                      value: p.id,
                      label: `Only ${p.name}`,
                    })),
                  ]}
                />
              </label>
              <label className="account-withdraw-field">
                <span>Anything the club should know (optional)</span>
                <textarea
                  data-testid="consent-withdraw-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  placeholder="Add context for the club"
                />
              </label>
              <p className="account-withdraw-note">
                The club will follow up before anything is changed or removed.
              </p>
            </div>
          }
        />
      ) : null}
    </section>
  );
}
